import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Button } from '@/components/ui/Button';
import { EmptyState, Skeleton } from '@/components/ui/Feedback';
import { Pill, PillGroup } from '@/components/ui/Pill';
import {
  courseNameFrom,
  useCourseIndex,
  usePlayerProfile,
  usePlayerRounds,
  useRoundsTogether,
} from '@/lib/api/queries';
import type { RoundSummary, SharedRound } from '@/lib/api/types';
import { ApiError } from '@/lib/api/errors';
import { fmtDateShort } from '@/lib/format/date';
import { fmtInt, fmtToPar } from '@/lib/format/number';
import { t } from '@/lib/i18n';
import { routes } from '@/routes/paths';
import styles from './PlayerRounds.module.css';
import { ChevronLeftIcon } from '@/components/icons';

const PAGE = 6;

type Filter = 'all' | 'together';

/**
 * As voltas de outro jogador (canvas 3a, "Voltas dele — lista completa").
 *
 * Mesma anatomia de linha da sua lista (1n), com uma coluna a mais: **o seu score naquela volta**,
 * quando vocês jogaram juntos. O filtro "Com você" é o recorte que o canvas aponta como o primeiro
 * que alguém vai querer — e o único que produz comparação honesta, porque é o mesmo campo no mesmo
 * dia, com o mesmo vento.
 *
 * Tocar numa volta abre o cartão dele em leitura: `GET /rounds/:id` já autoriza amigo, e a tela de
 * detalhe não oferece controle de edição a quem não é dono.
 */
export function PlayerRounds() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const profile = usePlayerProfile(userId);
  const rounds = usePlayerRounds(userId);
  const together = useRoundsTogether(userId);
  const courseIndex = useCourseIndex();

  const [filter, setFilter] = useState<Filter>('all');
  const [shown, setShown] = useState(PAGE);

  /** A volta **dele** dentro de cada flight compartilhado — a chave que liga as duas listas. */
  const sharedByRoundId = useMemo(() => {
    const map = new Map<string, SharedRound>();
    for (const round of together.data?.rounds ?? []) {
      if (round.theirs.roundId) map.set(round.theirs.roundId, round);
    }
    return map;
  }, [together.data]);

  const all = rounds.data?.rounds ?? [];
  const filtered = useMemo(
    () => (filter === 'together' ? all.filter((r) => sharedByRoundId.has(r.id)) : all),
    [all, filter, sharedByRoundId],
  );

  if (profile.error instanceof ApiError && profile.error.status === 403) {
    return (
      <div className={styles.page}>
        <Head onBack={() => navigate(-1)} name={null} />
        <div className={styles.body}>
          <EmptyState title={t('player.private.title')}>{t('player.private.body')}</EmptyState>
        </div>
      </div>
    );
  }

  if (rounds.isLoading || profile.isLoading) {
    return (
      <div className={styles.page}>
        <Head onBack={() => navigate(-1)} name={profile.data?.displayName ?? null} />
        <div className={styles.body}>
          <Skeleton height={56} radius={12} />
          <Skeleton height={56} radius={12} style={{ marginTop: 8 }} />
        </div>
      </div>
    );
  }

  const visible = filtered.slice(0, shown);
  const togetherCount = sharedByRoundId.size;

  return (
    <div className={styles.page}>
      <Head onBack={() => navigate(-1)} name={profile.data?.displayName ?? null} />

      <div className={styles.body}>
        <p className={styles.count}>
          {t('player.roundsCount', { count: all.length })}
          {togetherCount > 0 ? ` · ${t('player.ofWhichTogether', { count: togetherCount })}` : ''}
        </p>

        {togetherCount > 0 && (
          <PillGroup>
            <Pill selected={filter === 'all'} onClick={() => setFilter('all')}>
              {t('player.filterAll')}
            </Pill>
            <Pill selected={filter === 'together'} onClick={() => setFilter('together')}>
              {t('player.filterTogether')}
            </Pill>
          </PillGroup>
        )}

        {visible.length === 0 ? (
          <EmptyState title={t('player.noRounds')} />
        ) : (
          <ul className={styles.list}>
            {visible.map((round) => (
              <RoundRow
                key={round.id}
                round={round}
                shared={sharedByRoundId.get(round.id)}
                courseName={courseNameFrom(courseIndex.data, round.courseId)}
              />
            ))}
          </ul>
        )}

        {shown < filtered.length && (
          <div className={styles.more}>
            <p className={styles.moreCount}>
              {t('player.showing', { shown: visible.length, total: filtered.length })}
            </p>
            <Button variant="secondary" block onClick={() => setShown((n) => n + PAGE)}>
              {t('player.loadMore', { count: Math.min(PAGE, filtered.length - shown) })}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function Head({ onBack, name }: { onBack: () => void; name: string | null }) {
  return (
    <header className={styles.head}>
      <button
        type="button"
        className={styles.backButton}
        onClick={onBack}
        aria-label={t('rounds.back')}
      >
        <ChevronLeftIcon size={22} />
      </button>
      <div>
        {name && <div className={styles.headName}>{name}</div>}
        <h1 className={styles.headTitle}>{t('player.rounds')}</h1>
      </div>
    </header>
  );
}

function RoundRow({
  round,
  shared,
  courseName,
}: {
  round: RoundSummary;
  shared: SharedRound | undefined;
  courseName: string;
}) {
  return (
    <li>
      <Link to={routes.round(round.id)} className={styles.row}>
        <div className={styles.rowBody}>
          <div className={styles.rowDate}>{fmtDateShort(round.completedAt ?? round.startedAt)}</div>
          <div className={styles.rowWhere}>
            {t('player.rowWhere', {
              course: courseName,
              holes: round.holesPlayed,
              tee: round.teeBoxName,
            })}
          </div>
          {/* O seu score naquela volta — a coluna a mais que só existe aqui. */}
          {shared?.mine.gross != null && (
            <div className={styles.rowTogether}>
              {t('player.withYou', { value: fmtInt(shared.mine.gross) })}
            </div>
          )}
        </div>
        <div className={styles.rowScore}>
          <span className={styles.rowStrokes}>{fmtInt(round.totalStrokes)}</span>
          <span className={styles.rowToPar}>{fmtToPar(round.totalToPar)}</span>
        </div>
      </Link>
    </li>
  );
}
